import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { apiPost } from '../services/api';

/**
 * Modal de confirmação de exclusão com verificação de senha
 * 
 * @param {boolean} isOpen - Controla visibilidade do modal
 * @param {function} onClose - Função para fechar o modal
 * @param {function} onConfirm - Função async que executa a exclusão (recebe o item)
 * @param {object} item - Item a ser excluído
 * @param {string} itemName - Nome do item para exibição (ex: "turma 5º Ano A")
 * @param {string} itemType - Tipo do item (ex: "Turma", "Aluno", "Escola")
 * @param {string} warningMessage - Mensagem de aviso customizada (opcional)
 */
function DeleteConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    item,
    itemName,
    itemType = "item",
    warningMessage = "Esta ação não pode ser desfeita. Digite sua senha para confirmar."
}) {
    const { darkMode } = useTheme();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setPassword('');
        setError('');
        onClose();
    };

    const handleConfirm = async () => {
        if (!password) {
            setError('Digite sua senha');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Verifica a senha
            const verifyResponse = await apiPost('/verify-password/', { password });

            if (!verifyResponse.valid) {
                setError('Senha incorreta');
                setLoading(false);
                return;
            }

            // Executa a exclusão
            await onConfirm(item);
            handleClose();
        } catch (err) {
            console.error('Erro na exclusão:', err);

            // O apiPost armazena o status em err.status e os dados em err.response.data
            const errorStatus = err.status || err.response?.status;
            const errorData = err.response?.data || err.response || {};

            if (errorStatus === 401) {
                setError('Senha incorreta');
            } else if (errorStatus === 400) {
                // Erro de dependências
                const message = errorData.message || errorData.error || `Não é possível excluir ${itemType.toLowerCase()}`;
                const dependencies = errorData.dependencies?.join(', ') || '';
                const suggestion = errorData.suggestion || '';

                setError(
                    dependencies
                        ? `${message}: ${dependencies}. ${suggestion}`
                        : message
                );
            } else {
                setError(errorData.message || errorData.error || `Erro ao excluir ${itemType.toLowerCase()}`);
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className={`w-full max-w-md rounded-2xl p-6 ${darkMode
                ? 'bg-slate-800 border border-slate-700'
                : 'bg-white shadow-xl'
                }`}>
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-full bg-[#E83A3C]/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#E83A3C]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Confirmar Exclusão
                    </h2>
                </div>

                {/* Mensagem */}
                <p className={`mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Tem certeza que deseja excluir {itemType.toLowerCase()}{' '}
                    <strong className={darkMode ? 'text-slate-200' : 'text-slate-700'}>
                        {itemName}
                    </strong>?
                </p>

                {/* Aviso */}
                <div className={`p-3 rounded-lg mb-4 ${darkMode
                    ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20'
                    : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'
                    }`}>
                    <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>
                        ⚠️ {warningMessage}
                    </p>
                </div>

                {/* Campo de Senha */}
                <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        Sua Senha
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            setError('');
                        }}
                        placeholder="Digite sua senha para confirmar"
                        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                            ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                            : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                            } border focus:outline-none focus:ring-2 focus:ring-[#E83A3C]/50 ${error ? 'border-[#E83A3C]' : ''}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && password) {
                                handleConfirm();
                            }
                        }}
                        autoFocus
                    />
                    {error && (
                        <p className="text-[#E83A3C] text-sm mt-2">{error}</p>
                    )}
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        disabled={loading}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                            ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            }`}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || !password}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${loading || !password
                            ? 'bg-slate-500 cursor-not-allowed'
                            : 'bg-[#E83A3C] hover:bg-[#E83A3C]/80'
                            }`}
                    >
                        {loading ? 'Excluindo...' : `Excluir ${itemType}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DeleteConfirmModal;